import {EvalRowView, StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import * as Logging from 'util/Logging';

/**
 * Resolve a ROW_DELEGATE column to its delegated column for a given row.
 *
 * Combines the delegated column's type/properties with the ROW_DELEGATE column's identity
 * (provenanceUuid, columnGroup, isArchived, isReadOnly, isRequired). This preserves a unique
 * provenanceUuid per column position, which is required by consumers like LSUV tracking since
 * multiple ROW_DELEGATE columns can share the same delegated column.
 *
 * Returns the original column unchanged if it is not a ROW_DELEGATE or no delegation is found.
 */
export function resolveRowDelegateColumn<TRowMetadata>(
  column: StructuredTableColumn,
  row: EvalRowView<TRowMetadata>,
  delegatedColumns: ReadonlyArray<StructuredTableColumn>
): StructuredTableColumn {
  if (column.columnType !== TableColumnType.ROW_DELEGATE) {
    return column;
  }
  const delegatedColumnUuid = (row.metadata as RowMetadata).delegatedColumns?.[column.provenanceUuid];
  if (!delegatedColumnUuid) {
    Logging.warn(
      `No delegated column exists for column "${column.name}". Returning original delegate column.`
    );
    return column;
  }
  const delegatedColumn = delegatedColumns.find(
    (delegatedColumn) => delegatedColumn.provenanceUuid === delegatedColumnUuid
  );
  if (!delegatedColumn) {
    Logging.warn(
      `No delegated column exists for column "${column.name}". Returning original delegate column.`
    );
    return column;
  }
  // Combine aspects of the ROW_DELEGATE column and the delegated column.
  // Preserve the ROW_DELEGATE column's provenanceUuid so that each column position retains a
  // unique identity — multiple ROW_DELEGATE columns can share the same delegated column, and
  // consumers like LSUV tracking require uniqueness per column.
  return {
    ...delegatedColumn,
    provenanceUuid: column.provenanceUuid,
    columnGroup: column.columnGroup,
    isArchived: column.isArchived,
    isReadOnly: column.isReadOnly,
    isRequired: column.isRequired,
  };
}
